import { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const PHONE_NUMBER = '254788740000';

function WhatsAppLogo({ className = 'w-5 h-5' }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className={className}
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}

export default function WhatsAppFloatingIcon() {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    // StickyAddToCart is fixed at the bottom on product pages (mobile only) —
    // lift the FAB so it doesn't cover the product thumbnail.
    const isProductPage = location.pathname.startsWith('/product/');

    const pageUrl = useMemo(() => {
        return `${window.location.origin}${location.pathname}${location.search}`;
    }, [location.pathname, location.search]);

    const assistanceMessage = `I need assistance on ${pageUrl}`;

    const whatsappUrl = useMemo(() => {
        return `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(assistanceMessage)}`;
    }, [assistanceMessage]);

    const closePopup = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        closePopup();
    }, [location.pathname, location.search, closePopup]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closePopup();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closePopup]);

    const handleWhatsAppClick = () => {
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        closePopup();
    };

    return (
        <>
            {isOpen && (
                <button
                    type="button"
                    className="fixed inset-0 z-[9998] cursor-default bg-transparent border-0 p-0"
                    onClick={closePopup}
                    aria-label="Close WhatsApp popup"
                />
            )}

            <div
                className={`fixed left-4 z-[9999] flex flex-col items-start gap-3 lg:left-6 lg:bottom-6 ${
                    isProductPage ? 'bottom-24' : 'bottom-6'
                }`}
            >
                {isOpen && (
                    <div
                        className="w-[min(92vw,320px)] rounded-2xl bg-[#25D366] p-5 shadow-2xl"
                        role="dialog"
                        aria-label="WhatsApp assistance"
                        onClick={(event) => event.stopPropagation()}
                    >
                    <div className="flex items-start justify-between gap-3 mb-5">
                        <p className="text-white text-[15px] leading-snug font-medium pr-2">
                            Do you have any inquiries or need assistance?
                        </p>
                        <button
                            type="button"
                            onClick={closePopup}
                            className="shrink-0 text-white/80 hover:text-white transition-colors p-1 -mr-1 -mt-1"
                            aria-label="Close"
                        >
                            <i className="ri-close-line text-xl"></i>
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={handleWhatsAppClick}
                        className="w-full flex items-center justify-center gap-2.5 bg-[#f3f4f6] hover:bg-white text-gray-800 font-semibold rounded-full py-3.5 px-5 transition-colors shadow-sm"
                    >
                        <WhatsAppLogo className="w-5 h-5 text-[#25D366]" />
                        Whatsapp Us
                    </button>

                    <div className="flex items-center justify-center gap-2 mt-4">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" aria-hidden="true" />
                        <span className="text-white text-sm font-medium">we are online</span>
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                title="Chat with us on WhatsApp"
                aria-label={isOpen ? 'Close WhatsApp chat' : 'Open WhatsApp chat'}
                aria-expanded={isOpen}
                className="w-[60px] h-[60px] rounded-2xl bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform"
            >
                <WhatsAppLogo className="w-9 h-9" />
            </button>
            </div>
        </>
    );
}
